/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import styles from './styles.module.scss';
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import { useLabels } from '@/hooks/useLabels';
import { Text, View } from '@az/starc-ui';
import React from 'react';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { ReviewsOverviewContent } from './ReviewsOverviewContent';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { LoadingOverview } from './LoadingOverview/LoadingOverview';
import { formatNumber } from '../../utils/formatNumber';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const labelMap = {
  writeAReview: 'label_reviews_writeAReviewCTA',
};

export const ReviewsOverview = () => {
  const labels = useLabels(labelMap);
  const pdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const { data: productDetails } = useProductDetails({ enabled: false });
  const { data: myProfile } = useMyProfile();

  const product = productDetails?.product;
  const skuNumber = productDetails?.product?.itemId ?? '';

  const {
    data: reviewStatisticsData,
    isPending,
    isLoadingError,
  } = useReviewStatistics({
    skuNumbers: [skuNumber],
    enabled: Boolean(skuNumber),
  });

  const reviewStatistic = reviewStatisticsData?.[skuNumber];

  const reviewCount = formatNumber(reviewStatistic?.reviewsOnlyCount ?? 0) ?? '0';

  if (isLoadingError) {
    return null;
  }

  const recommendedCount = Number(reviewStatistic?.recommendedCount ?? 0);
  if (isPending) {
    return <LoadingOverview />;
  }

  return (
    <View>
      <View
        className={cx(styles.summary, {
          [styles['--fullWidth']]: pdpRedesignEnabled,
          [styles['--extraBottomMargin']]: !pdpRedesignEnabled,
        })}
      >
        <ReviewsOverviewContent
          reviewStatistic={reviewStatistic}
          reviewCount={reviewCount}
          recommendedCount={recommendedCount}
        />
        <View className={styles.bottomWriteReviewButton}>
          <SubmitReviewButton
            productId={product?.itemId}
            pageType="ProductDetail"
            defaultValues={{
              useremail: myProfile?.email,
              usernickname: myProfile?.firstName,
            }}
            label={
              <Text variant="small-body" weight="bold" className={styles.smallBody}>
                {labels.writeAReview}
              </Text>
            }
            variant="pill-secondary"
            productName={product?.itemDescription}
            productImageUrl={product?.itemImageUrl}
          />
        </View>
      </View>
    </View>
  );
};
